import React, { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useContent } from '../content/ContentContext';

interface EditableTextProps {
  path: string;                              // dotted content path, e.g. "hero.tagline"
  value: string;                             // current merged value
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'blockquote';   // element when NOT editing
  multiline?: boolean;
  className?: string;
}

// A single text field that renders normally for visitors and becomes an
// inline input/textarea in edit mode. Commits on blur (or Enter, single
// line). Reuses the field's own className so it keeps its typography.
export function EditableText({ path, value, as = 'span', multiline, className }: EditableTextProps) {
  const { editing } = useAuth();
  const { setField } = useContent();
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  if (!editing) {
    const As = as as React.ElementType;
    return <As className={className}>{value}</As>;
  }

  const commit = () => {
    if (draft !== value) setField(path, draft);
  };

  const cls = `editable ${multiline ? 'editable--multiline' : ''} ${className ?? ''}`.trim();

  return multiline ? (
    <textarea
      className={cls}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      rows={3}
      aria-label={path}
    />
  ) : (
    <input
      className={cls}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
      }}
      aria-label={path}
    />
  );
}
